// src/features/tasks/hooks/useCompletionApproval.js
import { useState, useCallback } from 'react';
import { completionApprovalAPI } from '../services/completionApproval';
import { useApi } from '../../../hooks/api/useApi';

export function useCompletionApproval() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approval, setApproval] = useState(null);
  const { execute } = useApi();

  /**
   * Fetch the adjusted completion date request for a task
   * @param {string} taskId - The task ID
   * @returns {Promise<Object|null>} The approval, or null if the task has none
   */
  const fetchApproval = useCallback(async (taskId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await execute(
        () => completionApprovalAPI.getApproval(taskId),
        { showErrorToast: false }
      );

      if (result.success) {
        setApproval(result.data.approval);
        return result.data.approval;
      }

      setError(result.error);
      return null;
    } catch (err) {
      setError(err.message || 'Failed to fetch completion date approval');
      return null;
    } finally {
      setLoading(false);
    }
  }, [execute]);

  const review = useCallback(async (taskId, decision, notes = null) => {
    setLoading(true);
    setError(null);

    const call = decision === 'approve' ? completionApprovalAPI.approve : completionApprovalAPI.reject;

    try {
      const result = await execute(
        () => call(taskId, { notes }),
        { showErrorToast: false }
      );

      if (result.success) {
        setApproval(result.data.approval);
        return { success: true, approval: result.data.approval };
      }

      const errorMsg = result.error || 'Failed to review completion date';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } catch (err) {
      const errorMsg = err.message || 'Failed to review completion date';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, [execute]);

  const approve = useCallback(
    (taskId, notes = null) => review(taskId, 'approve', notes),
    [review]
  );

  const reject = useCallback(
    (taskId, notes = null) => review(taskId, 'reject', notes),
    [review]
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    approval,
    loading,
    error,
    fetchApproval,
    approve,
    reject,
    clearError,
  };
}
